import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Stack, Typography } from '@mui/material';

import { CreditCardIcon, MplListItem, QuestionMarkOutlined } from '@mopla/ui';

import { InvoiceOptionItem } from './InvoiceOptionItem';

interface IMyTicketOptionsProps {
	/** Whether the cancellation entry is shown — preordered tickets only. */
	showCancellation: boolean;
	/**
	 * Whether the "Ticket als Chipkarte erhalten" entry is shown — only for a
	 * WendlandMobil Jahresticket paid via one-time (upfront) payment.
	 */
	showChipcard: boolean;
	/** Whether the help entry is shown — only when the BE sends an FAQ URL. */
	showHelp: boolean;
	/** Sends the paid invoice e-mail(s) for this purchase; rejects on failure. */
	onSendInvoice: () => Promise<void>;
	/** Called with the thrown error so the parent can show the error sheet. */
	onInvoiceError: (error: unknown) => void;
	onRequestHelp: () => void;
	onCancel: () => void;
	onRequestChipcard: () => void;
	/**
	 * TODO: Ticketshop – "Ticket entfernen" for expired/cancelled tickets.
	 * Kept commented out until BE exposes a remove endpoint (see the button
	 * block below).
	 */
	// showRemove: boolean;
	// onRemove: () => void;
}

/**
 * "Weitere Optionen" block: invoice + help list items (help only when the BE
 * sends an operator FAQ URL), plus the cancellation ("Stornierungs-Optionen")
 * entry shown only for preordered tickets.
 */
export const MyTicketOptions: FC<IMyTicketOptionsProps> = ({
	showCancellation,
	showChipcard,
	showHelp,
	onSendInvoice,
	onInvoiceError,
	onRequestHelp,
	onCancel,
	onRequestChipcard,
}) => {
	const { t } = useTranslation('ticketshop');

	return (
		<Stack gap={2} sx={{ width: '100%' }}>
			<Stack alignItems="center">
				<Typography variant="h4-sb" color="primary.main">
					{t('my_ticket_detail.options.title')}
				</Typography>
			</Stack>

			<Stack gap={2}>
				{showChipcard && (
					<MplListItem
						text={t('my_ticket_detail.options.chipcard')}
						leftIcon={<CreditCardIcon />}
						onClick={onRequestChipcard}
						dataTestid="my-ticket-detail-chipcard"
					/>
				)}
				<InvoiceOptionItem
					label={t('my_ticket_detail.options.invoice')}
					onSend={onSendInvoice}
					onError={onInvoiceError}
					dataTestid="my-ticket-detail-invoice"
				/>
				{showHelp && (
					<MplListItem
						text={t('my_ticket_detail.options.help')}
						leftIcon={<QuestionMarkOutlined />}
						onClick={onRequestHelp}
						dataTestid="my-ticket-detail-help"
					/>
				)}
			</Stack>

			{showCancellation && (
				<Button
					variant="text"
					onClick={onCancel}
					data-testid="my-ticket-detail-cancel"
					sx={{ height: 48, color: 'error.main', typography: 'bu1-m' }}
				>
					{t('my_ticket_detail.options.cancellation_options')}
				</Button>
			)}

			{/*
			 * TODO: Ticketshop – "Ticket entfernen" (remove ticket).
			 * Per Figma, expired/cancelled tickets show this instead of the
			 * cancellation option. Blocked on a BE endpoint to remove a purchased
			 * ticket (e.g. DELETE /api/v1/me/tickets/{purchaseId}) — no such
			 * operation exists in the ticketshop-api spec yet. Once available:
			 *   1. add `showRemove` + `onRemove` to the props above,
			 *   2. wire `onRemove` in MyTicketDetail to call the endpoint and
			 *      refresh the list (useRefreshMyTickets) + navigate back,
			 *   3. uncomment the block below.
			 *
			 * {showRemove && (
			 * 	<Button
			 * 		variant="text"
			 * 		onClick={onRemove}
			 * 		data-testid="my-ticket-detail-remove"
			 * 		sx={{ height: 48, color: 'error.main', typography: 'bu1-m' }}
			 * 	>
			 * 		{t('my_ticket_detail.options.remove_ticket')}
			 * 	</Button>
			 * )}
			 */}
		</Stack>
	);
};
